import { XClose } from '@/assets/icons'
import { ModalTitle, ModalClose, ModalDescription } from '@/components/modal/Modal'
import StateContent from './StateContent/StateContent'
import { JURISDICTION_TABS } from '../constants'
import FederalContent from './FederalContent/FederalContent'
import { useAppSelector } from '@/store/store-hooks'
import { selectNumberOfCurrentSourcesShownInFederalTab, selectResearchFiltersDisplayForStates } from '../store/selectors'
import { RootState } from '@/store/store'
import { StatesFilterOptions } from './StateContent/StatesFilterOptions'
import { StateSelectionProps } from '../types'

type DesktopDialogOptionsProps = {
  activeTab: string
  handleTabChange: (tab: string) => void
  onOpenChange: (open: boolean) => void
  conversationId: string
  showWarningBanner: boolean
  stateSelectionProps: StateSelectionProps
}

export default function DesktopDialogOptions(props: DesktopDialogOptionsProps) {
  const { activeTab, handleTabChange, onOpenChange, conversationId, stateSelectionProps, showWarningBanner } = props

  // Get description from filters UI state (reflects current selections in dialog)
  const displayResearchSourcesDescription = useAppSelector((state: RootState) => selectResearchFiltersDisplayForStates(state, { conversationId }))

  const numberOfCurrentSourcesShownInFederalTab = useAppSelector((state: RootState) => selectNumberOfCurrentSourcesShownInFederalTab(state, { conversationId }))

  const handleKeyDown = (e: React.KeyboardEvent, tab: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleTabChange(tab)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 bg-brand-neutral-50 z-10 border-b border-brand-neutral-200 py-3 rounded-t-2xl">
        <div className="grid grid-cols-[1fr_auto] items-center px-4">
          <div>
            <ModalTitle className="text-brand-neutral-950 text-lg font-semibold text-center font-rethink-sans">Research Sources</ModalTitle>
            <ModalDescription className="text-brand-neutral-600 text-sm font-normal text-center">
              Select at least one source to enable case law, statutory, and regulatory research.
            </ModalDescription>
          </div>
          <ModalClose asChild>
            <button
              className="w-7 h-7 rounded-full flex items-center justify-center text-brand-neutral-600 hover:bg-brand-neutral-200 focus:outline-none transition-all duration-300"
              aria-label="Close"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              <XClose className="size-6" />
            </button>
          </ModalClose>
        </div>
      </div>

      {/* Body with Sidebar */}
      <div className="flex-1 overflow-hidden grid grid-cols-[200px_1fr]">
        {/* Sidebar Tabs */}
        <ul role="tablist" aria-label="Research source types">
          <li
            id="states-tab"
            role="tab"
            aria-selected={activeTab === JURISDICTION_TABS.STATES}
            aria-controls="states-tabpanel"
            tabIndex={activeTab === JURISDICTION_TABS.STATES ? 0 : -1}
            key="states_tab"
            className={`flex flex-col w-full font-semibold text-sm text-left  font-rethink-sans hover:bg-brand-neutral-100 cursor-pointer min-h-[64px] px-3 justify-center ${
              activeTab === JURISDICTION_TABS.STATES ? 'text-brand-500 bg-brand-neutral-100' : 'text-brand-neutral-800'
            }`}
            onClick={() => handleTabChange(JURISDICTION_TABS.STATES)}
            onKeyDown={(e) => handleKeyDown(e, JURISDICTION_TABS.STATES)}
          >
            <span>States</span>
            {displayResearchSourcesDescription !== '' && (
              <span className="text-brand-neutral-500 font-normal text-xs mt-1">{displayResearchSourcesDescription}</span>
            )}
          </li>
          <li
            id="federal-tab"
            role="tab"
            aria-selected={activeTab === JURISDICTION_TABS.FEDERAL}
            aria-controls="federal-tabpanel"
            tabIndex={activeTab === JURISDICTION_TABS.FEDERAL ? 0 : -1}
            key="federal_tab"
            className={`flex flex-col w-full font-semibold text-sm text-left font-rethink-sans hover:bg-brand-neutral-100 cursor-pointer border-b border-t border-brand-neutral-200 min-h-[64px] px-3 justify-center ${
              activeTab === JURISDICTION_TABS.FEDERAL ? 'text-brand-500 bg-brand-neutral-100' : 'text-brand-neutral-800'
            }`}
            onClick={() => handleTabChange(JURISDICTION_TABS.FEDERAL)}
            onKeyDown={(e) => handleKeyDown(e, JURISDICTION_TABS.FEDERAL)}
          >
            <span>Federal</span>
            {numberOfCurrentSourcesShownInFederalTab > 0 && (
              <span className="text-brand-neutral-500 font-normal text-xs mt-1">
                {numberOfCurrentSourcesShownInFederalTab}
                {numberOfCurrentSourcesShownInFederalTab > 1 ? ' selections' : ' selection'}
              </span>
            )}
          </li>
        </ul>

        {/* Content Area */}
        <div className="overflow-y-auto border-l border-brand-neutral-200">
          {/* Conditional rendering content based on active tab */}
          {activeTab === JURISDICTION_TABS.STATES && (
            <div id="states-tabpanel" role="tabpanel" aria-labelledby="states-tab">
              <StateContent conversationId={conversationId} stateSelectionProps={stateSelectionProps} />
              <StatesFilterOptions
                conversationId={conversationId}
                hasSelectedStates={stateSelectionProps.selectedStates.length > 0}
                includeRelatedFederalContent={stateSelectionProps.includeRelatedFederalContent}
                setShowWarningBanner={stateSelectionProps.setShowWarningBanner}
                includeUnpublishedCases={stateSelectionProps.includeUnpublishedCases}
                showWarningBanner={showWarningBanner}
              />
            </div>
          )}
          {activeTab === JURISDICTION_TABS.FEDERAL && (
            <div id="federal-tabpanel" role="tabpanel" aria-labelledby="federal-tab">
              <FederalContent conversationId={conversationId} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
